import type {
  BucketName,
  PresignedUrl,
  S3BucketMapping,
} from "@atoms/atom-types";
import {
  buildPublicFileUrlFromMappings,
  encodeObjectKeyPath,
  readS3BucketMappingsFromEnv,
  readS3FqdnFromEnv,
  resolvePublicDeployedBucketName,
} from "./s3PublicUrls";

let bucketMappingsCache: S3BucketMapping[] | null = null;

function getBucketMappings(): S3BucketMapping[] {
  bucketMappingsCache ??= readS3BucketMappingsFromEnv();
  return bucketMappingsCache;
}

/**
 * Builds a public object URL from explicit FQDN and bucket mappings.
 *
 * Convenience wrapper around {@link buildPublicFileUrlFromMappings}.
 */
export function buildPublicFileUrl(
  s3Fqdn: string,
  bucketMappings: S3BucketMapping[],
  bucket: BucketName,
  objectKey: string,
): string | undefined {
  return buildPublicFileUrlFromMappings(
    s3Fqdn,
    bucketMappings,
    bucket,
    objectKey,
  );
}

/**
 * Builds a public object URL using frontend `S3_FQDN` and `S3_BUCKETS` env vars.
 *
 * Use this when atom state stores only the object key, not a full URL.
 *
 * @param bucket - Semantic bucket name, e.g. from generated `@project/shared` constants
 * @param objectKey - Object key within the bucket
 * @returns A fully qualified public URL, or `undefined` when env or key is missing
 */
export function getPublicFileUrl(
  bucket: BucketName,
  objectKey: string,
): string | undefined {
  const s3Fqdn = readS3FqdnFromEnv();
  if (!s3Fqdn) {
    return undefined;
  }
  return buildPublicFileUrl(s3Fqdn, getBucketMappings(), bucket, objectKey);
}

/**
 * Builds a public bucket base URL from explicit FQDN and bucket mappings.
 *
 * Useful when many assets share a common prefix, e.g. `${base}/models/mesh.glb`.
 *
 * @param pathPrefix - Optional path segment appended after the bucket name
 */
export function buildPublicBucketBaseUrl(
  s3Fqdn: string,
  bucketMappings: S3BucketMapping[],
  bucket: BucketName,
  pathPrefix?: string,
): string | undefined {
  if (!s3Fqdn) {
    return undefined;
  }

  const actualBucket = resolvePublicDeployedBucketName(bucket, bucketMappings);
  if (!actualBucket) {
    return undefined;
  }

  const base = `${s3Fqdn.replace(/\/$/, "")}/${actualBucket}`;
  if (!pathPrefix) {
    return base;
  }

  const encodedPrefix = encodeObjectKeyPath(
    stripSurroundingSlashes(pathPrefix),
  );
  return encodedPrefix ? `${base}/${encodedPrefix}` : base;
}

/**
 * Builds a public bucket base URL using frontend `S3_FQDN` and `S3_BUCKETS` env vars.
 *
 * @param bucket - Semantic bucket name
 * @param pathPrefix - Optional path segment appended after the bucket name
 */
export function getPublicBucketBaseUrl(
  bucket: BucketName,
  pathPrefix?: string,
): string | undefined {
  const s3Fqdn = readS3FqdnFromEnv();
  if (!s3Fqdn) {
    return undefined;
  }

  return buildPublicBucketBaseUrl(
    s3Fqdn,
    getBucketMappings(),
    bucket,
    pathPrefix,
  );
}

export type ToObjectKeyOptions = {
  /**
   * Public S3 base URL. Defaults to frontend `S3_FQDN`.
   */
  s3Fqdn?: string;
  /**
   * Semantic-to-deployment bucket mappings. Defaults to frontend `S3_BUCKETS`.
   */
  bucketMappings?: S3BucketMapping[];
};

function stripLeadingSlashes(value: string): string {
  let start = 0;
  while (start < value.length && value[start] === "/") {
    start += 1;
  }
  return value.slice(start);
}

function stripSurroundingSlashes(value: string): string {
  let start = 0;
  let end = value.length;

  while (start < end && value[start] === "/") {
    start += 1;
  }
  while (end > start && value[end - 1] === "/") {
    end -= 1;
  }

  return value.slice(start, end);
}

function getPathRelativeToBase(pathname: string, basePath: string): string {
  if (basePath.length === 0) {
    return pathname;
  }

  if (!pathname.startsWith(`${basePath}/`)) {
    return "";
  }

  return pathname.slice(basePath.length + 1);
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function getPublicDeployedBucketNames(
  mappings: S3BucketMapping[],
): Set<string> {
  return new Set(
    mappings
      .filter((mapping) => mapping.public)
      .map((mapping) => String(mapping.bucketDeployment)),
  );
}

/**
 * Extracts an object key from a value that may be a full URL or already an object key.
 *
 * For configured S3 path-style URLs shaped like `{fqdn}/{bucket}/{objectKey}`,
 * returns `{objectKey}`. Non-URL values and unrelated absolute URLs are returned unchanged.
 */
export function toObjectKey(
  value: string,
  options?: ToObjectKeyOptions,
): string {
  if (!/^https?:\/\//i.test(value)) {
    return value;
  }

  try {
    const url = new URL(value);
    const s3Fqdn = options?.s3Fqdn ?? readS3FqdnFromEnv();
    if (!s3Fqdn) {
      return value;
    }

    const s3BaseUrl = new URL(s3Fqdn);
    if (url.origin !== s3BaseUrl.origin) {
      return value;
    }

    const basePath = stripSurroundingSlashes(s3BaseUrl.pathname);
    const pathname = stripLeadingSlashes(url.pathname);
    const relativePath = getPathRelativeToBase(pathname, basePath);
    const segments = relativePath.split("/");
    if (segments.length >= 2) {
      const bucketName = segments[0];
      if (!bucketName) {
        return value;
      }

      const bucketMappings = options?.bucketMappings ?? getBucketMappings();
      const publicDeployedBucketNames =
        getPublicDeployedBucketNames(bucketMappings);
      if (
        publicDeployedBucketNames.size === 0 ||
        !publicDeployedBucketNames.has(bucketName)
      ) {
        return value;
      }

      return segments.slice(1).map(decodePathSegment).join("/");
    }
  } catch {
    return value;
  }

  return value;
}

/** Options for resolving stored values to public URLs. */
export type ResolveStorageUrlOptions = {
  /**
   * Maps an object key to its semantic bucket.
   * Use when bucket choice depends on key prefix or other app logic.
   */
  resolveBucket?: (objectKey: string) => BucketName;
  /**
   * Fallback bucket when `resolveBucket` is not provided.
   * Use when all values in a field belong to the same bucket.
   */
  defaultBucket?: BucketName;
};

/**
 * Resolves a stored storage value to a displayable public URL.
 *
 * - Returns `null` for empty values
 * - Returns absolute `http(s)://` URLs unchanged (legacy full URLs)
 * - Resolves object keys via {@link getPublicFileUrl} using `resolveBucket` or `defaultBucket`
 *
 * @returns A public URL suitable for `<img src>` etc., or `null` when unresolvable
 */
export function resolveStorageUrl(
  value: string | null | undefined,
  options?: ResolveStorageUrlOptions,
): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const bucket =
    options?.resolveBucket?.(trimmed) ?? options?.defaultBucket ?? null;
  if (!bucket) {
    return null;
  }

  return getPublicFileUrl(bucket, trimmed) ?? null;
}

/**
 * Uploads a file to S3/MinIO using a presigned URL from a backend reducer.
 *
 * @param presigned - URL and HTTP method returned by `IStorageService.getUploadUrl`
 * @param body - File contents to upload
 * @param contentType - Optional `Content-Type` header sent with the request
 * @throws When the upload response is not OK
 */
export async function putFileToPresignedUrl(
  presigned: Pick<PresignedUrl, "url" | "method" | "headers">,
  body: Blob,
  contentType?: string,
): Promise<void> {
  const headers = {
    ...presigned.headers,
    ...(contentType ? { "Content-Type": contentType } : {}),
  };
  const response = await fetch(presigned.url, {
    method: presigned.method,
    body,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}

/**
 * Deletes an object via a presigned URL from a backend reducer.
 *
 * Typically used with rollback URLs returned alongside upload presigns.
 *
 * @param presigned - URL and HTTP method returned by `IStorageService.getDeleteUrl`
 * @throws When the delete response is not OK
 */
export async function deleteViaPresignedUrl(
  presigned: Pick<PresignedUrl, "url" | "method">,
): Promise<void> {
  const response = await fetch(presigned.url, {
    method: presigned.method,
  });

  if (!response.ok) {
    throw new Error(`Delete failed with status ${response.status}`);
  }
}
